import {Component, OnInit, inject, DestroyRef, Input} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Instance } from 'src/app/models/instance/instance';
import { Blueprint } from 'src/app/models/blueprints/blueprint';
import {translate} from '@jsverse/transloco';
import { Solution } from 'src/app/models/service/solution';
import { SolutionVersion } from 'src/app/models/service/solution-version';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { RouterSegments } from 'src/app/models/router-segments';
import { Permission } from 'src/app/models/principal/permission';
import {MatDialog} from '@angular/material/dialog';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { InstancesService } from 'src/app/services/api/instances.service';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ComponentService } from 'src/app/services/api/component.service';
import {InstallationSpecificationService} from 'src/app/services/util/installation-specification.service';
import { ComponentType } from 'src/app/models/service/component-type';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { SizingService } from 'src/app/services/api/sizing.service';

const REQUIRED_PERMISSIONS = [
  Permission.CREATE_SANDBOX,
  Permission.CREATE_SERVICE_DEFINITION,
  Permission.VIEW_ENTERPRISE,
];

@Component({
  selector: 'app-resource-configuration-content',
  templateUrl: './resource-configuration-content.component.html',
  styleUrl: './resource-configuration-content.component.scss',
  standalone: false
})
export class ResourceConfigurationContentComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  service: Solution;
  blueprint: Blueprint;
  instances: Instance[];
  RouterSegments = RouterSegments;
  tenant: string;
  breadcrumbItems: BreadcrumbItem[];
  permissionsMap: { [key: string]: boolean };
  ComponentType = ComponentType;
  serviceId: any;
  instance: any;
  instanceId: string;
  errorMessage: any;
  solutionVersion: any;
  isLoading = true;
  tbcComponents = [];
  exchangeComponents = [];
  tbc = ComponentType.TBC;
  exchange = ComponentType.EXCHANGE;
  review: any = 'review';
  activePage = ComponentType.TBC;
  nextPage = ComponentType.EXCHANGE;
  installationSpecificationForms: any;
  activeForm = false;
  solution: SolutionVersion;
  version: string;
  allInstances: any;
  sortedSolutionVersionId: any;
  tenantId: string;
  parameters: any;
  isComponentLoaded = false;
  @Input() instancesId!: string;
  @Input() blueprintId!: string;
  stepsOrder = [this.tbc, this.exchange, this.review];
  sizingDocumentId: string;

  readonly INSTANCE_AVAILABLE_STATUS = [DeployStatus.DEPLOYED, DeployStatus.UPDATED,
      DeployStatus.UPDATE_FAILED, DeployStatus.TESTED, DeployStatus.TEST_FAILED,
      DeployStatus.CONFIGURED, DeployStatus.CONFIGURATION_FAILED,
      DeployStatus.FAILED, DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, DeployStatus.PRE_FAILBACK_FAILED, DeployStatus.POST_FAILBACK_FAILED, DeployStatus.STOP_FAILED];
  
    constructor(
      private activatedRoute: ActivatedRoute,
      private router: Router,
      private solutionsService: SolutionsService,
      private blueprintsService: BlueprintsService,
      private authService: AuthService,
      private permissionsService: PermissionsService,
      private instancesService: InstancesService,
      private componentService: ComponentService,
      private installationSpecificationService: InstallationSpecificationService,
      private dialog: MatDialog,
      private toastrService: CustomToastrService,
      private sizingService: SizingService
    ) {
      
    }
  
    ngOnInit(): void {
      this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
      this.tenant = this.authService.principal.provider;
      this.sizingDocumentId = this.sizingService.getSizingDocumentId();      
      this.getParameters();
    }
  
    getParameters(): any {
      this.instanceId = this.activatedRoute.snapshot.params.id;
      this.instancesService.getInstanceParameters(this.instanceId).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.parameters = data?.parameters;
        this.loadData();
      }, (error) => {
          setTimeout(() => {
            const title = translate('ERRORS.SERVICE_AVAILABILITY');
            const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
            this.toastrService.errMessage(message, title);
          }, 500);
        this.loadData();
      });
    }

    isCurrent(step: string): boolean {
      return this.activePage === step;
    }

    isVisited(step: string): boolean {
      const currentIndex = this.stepsOrder.indexOf(this.activePage);
      const stepIndex = this.stepsOrder.indexOf(step);

      if (step === this.review) {
        return false;
      }

    return stepIndex < currentIndex;
  }
  
    loadData(): void {
      const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
      this.instancesService.getInstanceById(this.instanceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (instance) => {
        this.instance = instance;
        if (this.instance.status !== DeployStatus.FAILED && this.instance.status !== DeployStatus.INITIALIZED &&  this.instance.status !== DeployStatus.INITIALIZING) {
          this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , this.instance.tenantId, RouterSegments.BLUEPRINTS, blueprintId])
        }
        this.isLoading = false;
        this.tenantId = instance.tenantId;        
        this.blueprintsService.getBlueprint(blueprintId).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(blueprint => {
          this.blueprint = blueprint;
          this.configureBreadcrumb();
        });
        this.solutionsService.getSolutionVersion(this.instance.solutionId, this.instance.solutionVersionId).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          this.solution = data;
          this.version = data?.version;
          if (this.version) {
            this.blueprintsService.getBlueprintVersionById(blueprintId, this.instance.blueprintVersionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (blueprint) => {
              if (blueprint?.capabilities) {
                blueprint.capabilities?.forEach(capability => {
                  const version = data?.capabilities.find(item=> item?.name === capability?.name)?.version; 
                  if (capability?.componentType === this.exchange) {
                    this.exchangeComponents.push({name: capability?.name, displayName: capability?.displayName, version: version, 'active': false, dataReceived: false});
                  } else {
                    this.tbcComponents?.push({name: capability?.name, displayName: capability?.displayName, version: version, 'active': false, dataReceived: false});
                    if (this.tbcComponents?.length && this.tbcComponents[0]) {
                      this.getComponent(this.tbcComponents[0], this.tbc);
                    }
                  }
                })
              }
            })
          }
        });
      });
    }
  
    getComponent(component, type): void {
      if (component) {
        if (type === this.tbc) {
          this.tbcComponents.forEach(item => item.active = false);
          component.active = true;
        } else if (type === this.exchange) {
          this.exchangeComponents.forEach(item => item.active = false);
          component.active = true;
        }    
        if (!component.dataReceived) {
          this.componentService.getComponentsNameVersion(component.name, component.version).subscribe((result) => {
            this.isComponentLoaded = true;
            if (result?.parameterSpec) {
              this.installationSpecificationForms = this.installationSpecificationService.buildDynamicForms(
                atob(result.parameterSpec)
              );
              const installDocRaw = this.parameters?.components?.find(item => item.name === component.name)?.parameterDoc;
              if (installDocRaw) {
                this.getPreviousVersionData(installDocRaw);
              } else {
                this.installationSpecificationForms.forEach(nestedForm => {
                  nestedForm?.property?.properties?.forEach(element => {
                    if (element['x-value-type'] === 'secret') {
                      element.hide = true;
                    }
                  });
                });
              }
            } else {
              this.installationSpecificationForms = component.form;
            }
    
            component.form = this.installationSpecificationForms;
            this.activeForm = true;
          }, () => {
            this.isComponentLoaded = true;
          });
          component.dataReceived = true;
        } else {
          this.installationSpecificationForms = component.form;
        }
      }
    }
  
    goBack(): void {
      const isUpdate = (this.INSTANCE_AVAILABLE_STATUS.includes(this.instance.status) && !this.parameters.componentParameterStatus);
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: isUpdate ? translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.UPDATE_CANCEL_TITLE') : translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.CANCEL_TITLE'),
          message: isUpdate? translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.UPDATE_CANCEL_MESSAGE') : translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.CANCEL_MESSAGE'),
          confirmButtonText: translate('BUTTONS.CONFIRM')
        }
      })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(confirm => {
          if (confirm) {
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.blueprint?.id]);
          }
        });
    }
  
    previous(page): void {
      
      if (page === this.tbc) {
        this.goBack();
        return;
      } 
      else if (page === this.exchange) {
        this.activePage = this.tbc;
        this.nextPage = this.exchange;
      } 
      else if (page === this.review) {
        this.activePage = this.exchange;
        this.nextPage = this.review;
      }

      if (this.tbcComponents?.length > 0) {
        this.tbcComponents[0].dataReceived = false;
      }

      if (page === this.exchange && this.tbcComponents?.length > 0) {
        this.getComponent(this.tbcComponents[0], this.tbc);
      }

      if (page === this.review && this.exchangeComponents?.length > 0) {
        this.getComponent(this.exchangeComponents[0], this.exchange);
      }
    }
  
    continue(page): void {
      if(page === this.tbc) {
        this.activePage = this.exchange;
        this.nextPage = this.review;
        this.getComponent(this.exchangeComponents[0], this.exchange);
      } else if(page === this.exchange) {
        this.activePage = this.review;
      } else if(page === this.review) {
        this.activePage = this.review;
        this.onConfigure(event);
      }
    }
  
    onEdit(page): void {
      if(page === this.tbc) {
        this.activePage = this.tbc;
        this.nextPage = this.exchange;
        this.getComponent(this.tbcComponents[0], this.tbc);
      } else if(page === this.exchange) {
        this.activePage = this.exchange;
        this.nextPage = this.review;
        this.getComponent(this.exchangeComponents[0], this.exchange);
      }
    }
  
    skip(page): void {
      if(page === this.tbc) {
        this.activePage = this.exchange;
        this.nextPage = this.review;
        this.tbcComponents.forEach((item) => {
          item.active = false;
          item.dataReceived = false;
          item.form = ''
        });
        this.getComponent(this.tbcComponents[0], this.exchange);
      } else if(page === this.exchange) {
        this.exchangeComponents.forEach((item) => {
          item.active = false;
          item.dataReceived = false;
          item.form = ''
        });
        this.getComponent(this.exchangeComponents[0], this.exchange);
        this.activePage = this.review;
      } else if(page === this.review) {
        this.activePage = this.review;
        this.onConfigure(event);
      }
    }
  
    onConfigure(event): void {
      const payload = this.getFilledInstallationDocument();
      event.stopPropagation();
      this.dialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          title: translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.TITLE'),
          message: translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.SUBTITLE'),
          confirmButtonText: translate('DIALOG.SUBMIT')
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          const obj = {
            id: this.instanceId,
            sizingDocumentId: payload.sizingDocumentId,
            parameters: payload.parameters
          };
          this.instancesService.putInstanceProvision(obj)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((data) => {
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT, this.tenantId, RouterSegments.BLUEPRINTS, this.blueprint?.id]);
            setTimeout(() => {
              const title = translate('TENANT_DASHBOARD.INSTANCE');
              const message = translate('NOTIFICATIONS.DEPLOY_SUCCESS');
              this.toastrService.successMessage(message, title);
            }, 500);
          }, (error) => {
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title:  translate('INSTANCE_DETAILS.RESOURCE_CONFIGURATION.CONFIGURATION_FAILED'),
                description: errorMessage
              }
            });
          });
        }
      });
    }
  
    private getFilledInstallationDocument(): any {
      let tbcComponents = [];
      let exchangeComponents = [];
      let tbcInstallationDocument = {};
      let exchangeInstallationDocument = {};
      let parameters = {}
      this.tbcComponents?.forEach(configForm => {
        configForm.form?.forEach(form => {
          const installationSpecificationFormValue = this.installationSpecificationService.getFormValue(
            form);
          Object.assign(tbcInstallationDocument, installationSpecificationFormValue);
          tbcComponents = [...tbcComponents, {name: configForm.name, parameterDoc: installationSpecificationFormValue}];
        });
      });
      this.exchangeComponents?.forEach(configForm => {
        configForm.form?.forEach(form => {
          const installationSpecificationFormValue = this.installationSpecificationService.getFormValue(
            form);
          Object.assign(exchangeInstallationDocument, installationSpecificationFormValue);
          exchangeComponents = [...exchangeComponents, {name: configForm.name, parameterDoc: installationSpecificationFormValue}];
        });
      });
      const configuredData = [ ...tbcComponents, ...exchangeComponents];
      return {
      sizingDocumentId: this.sizingDocumentId,
        parameters: {
          components: configuredData
        }
      };
    }
  
    private configureBreadcrumb(): void {
      this.breadcrumbItems = [];
      this.breadcrumbItems.push({label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION]},
      {label: this.blueprint?.name, url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION, this.blueprint?.id]});
    }
  
    private getPreviousVersionData(installDocRaw): void {
      Object.keys(installDocRaw).forEach(key => {
        const propertyKey = Object.keys(installDocRaw[key]);
        const propertyValue = Object.assign(installDocRaw[key]);
        propertyKey.forEach(controlName => {
          this.installationSpecificationForms.forEach(nestedForm => {
            nestedForm?.formGroup?.controls[key]?.get(controlName)?.setValue(
              propertyValue[controlName].length === undefined ? '' : propertyValue[controlName]);
            nestedForm?.property?.properties?.forEach(element => {
              if (nestedForm?.formGroup?.controls[key]?.get(element.name)) {
                element.updatedValue = nestedForm.formGroup.controls[key].get(element.name).value;
                if (element['x-value-type'] === 'secret' && element.updatedValue) {
                  element.hide = true;
                  element.readonly = true;
                } else if (element['x-value-type'] === 'secret') {
                  element.hide = true;
                }
              }
            });
          });
        });
      });
    }
}
